import type { TTimelineItem } from "../types";
import { projectTimelineSegments, type TrackingTimeline } from "../tracking/player/timeline-status";
import { collectRenderableSegments } from "./tree";

// Ignore seek-clock rounding without skipping short, frame-sized fragments.
const SEGMENT_NEIGHBOR_GAP_S = 0.000002;

function displayedSegments(items: readonly TTimelineItem[], timeline: TrackingTimeline | null) {
  // The root video row is the scrub surface, not an annotation. Collections
  // recurse into the same logical segments whether their rows are expanded or collapsed.
  return projectTimelineSegments(collectRenderableSegments(items.filter((item) => item.type !== "video")), timeline);
}

export function segmentStartTimes(items: readonly TTimelineItem[], timeline: TrackingTimeline | null = null): number[] {
  const segments = displayedSegments(items, timeline);
  return [
    ...new Set(segments.map((segment) => segment.start).filter((start) => Number.isFinite(start) && start >= 0)),
  ].sort((a, b) => a - b);
}

/** Ready/pending boundaries stay internal; terminal gaps create separate visible segments. */
export function segmentBoundaryTimes(
  items: readonly TTimelineItem[],
  timeline: TrackingTimeline | null = null,
  duration = Infinity,
): number[] {
  return [
    ...new Set(
      displayedSegments(items, timeline)
        .flatMap((segment) => [segment.start, segment.end ?? segment.start])
        .filter((time) => Number.isFinite(time) && time >= 0 && time <= duration),
    ),
  ].sort((a, b) => a - b);
}

export function adjacentSegmentTime(
  starts: readonly number[],
  currentTime: number,
  direction: -1 | 1,
  duration: number,
): number | undefined {
  const end = Number.isFinite(duration) && duration > 0 ? duration : Infinity;
  if (direction === -1) {
    for (let index = starts.length - 1; index >= 0; index--) {
      const start = starts[index]!;
      if (start < end && start < currentTime - SEGMENT_NEIGHBOR_GAP_S) return start;
    }
    return 0;
  }
  return (
    starts.find((start) => start < end && start > currentTime + SEGMENT_NEIGHBOR_GAP_S) ??
    (Number.isFinite(duration) && duration > 0 ? duration : undefined)
  );
}
